import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsManagerView } from '@plato/app-core/hooks/use-view-mode';
import { usePersistedQueryParams } from '@plato/app-shared/hooks/use-persisted-query-params';
import {
    enumParam,
    generateQueryParamsString,
    objectParam,
    queryParamsConfig,
    requiredParam,
    stringArrayParam,
    stringParam,
    transientParam,
} from '@plato/ui-legacy/lib/query-params';
import type { GetManyCustomersFilters } from '../../../server/types/infer-trpc-types.ts';
import { CustomerSortBy, SortOrder } from '../../../server/types/list-view-types.ts';
import {
    defaultSortFilter,
    mapSortFilterAccordingToUserPreferences,
} from '../constants/customer-list-filters.ts';

const config = queryParamsConfig<GetManyCustomersFilters>({
    cityNames: stringArrayParam(),
    industryNames: stringArrayParam(),
    zipCodes: stringArrayParam(),
    insightTypes: stringArrayParam(),
    searchString: transientParam(stringParam()),
    salesRepIds: stringArrayParam(),
    statuses: stringArrayParam(),
    companyIds: stringArrayParam(),
    customerClasses: stringArrayParam(),
    salesRegions: stringArrayParam(),
    customerIds: stringArrayParam(),
    sortingFilter: objectParam({
        field: requiredParam(enumParam(CustomerSortBy)),
        direction: requiredParam(enumParam(SortOrder)),
    }),
});

const defaultFilters: GetManyCustomersFilters = {
    cityNames: [],
    industryNames: [],
    zipCodes: [],
    insightTypes: [],
    searchString: '',
    salesRepIds: [],
    statuses: [],
    companyIds: [],
    customerClasses: [],
    salesRegions: [],
    customerIds: [],
    sortingFilter: defaultSortFilter,
};

export function generateSearchParamsForCustomerList(filters: Partial<GetManyCustomersFilters>): string {
    return generateQueryParamsString(config, filters);
}

export function useCustomerListFilters() {
    const isManagerView = useIsManagerView();
    const [actualFilters, setAppliedFilters] = usePersistedQueryParams(config, defaultFilters, 'customers');
    const { defaultTimeframe } = useAppConfig();

    const appliedFilters = {
        ...defaultFilters,
        ...actualFilters,
    };

    const sortFilter = mapSortFilterAccordingToUserPreferences(appliedFilters, {
        isManagerView,
        defaultTimeframe,
    });

    appliedFilters.sortingFilter = sortFilter;

    return {
        appliedFilters,
        setAppliedFilters,
        defaultFilters,
    };
}
